import React, { useMemo } from 'react';
import { BaseCard } from '@/components/uiKit/Cards';
import { CardDropdown } from '@/components/uiKit/Dropdown/CardDropdown';
import { dispatch, RootState } from '@/store/store';
import { setChartGroupBy } from '@/store/slices/assetsPreviewSlice/alarmsSlice';
import { useSelector } from 'react-redux';
import { Skeleton } from '@/components/ui/skeleton';
import { StackedBarChartEcharts } from '@/modules/shared/Charts';
import { chartItems } from './constants';
import { EmptyState } from '../shared/EmptyState';
import { renderTopAlarmsTooltip } from '../utils/utils.alarms-chart-tooltips';
import { generateComponentColorMap } from '../utils';

export function TopAlarmsBarChartCard({
  chartData,
  isLoading,
  hasTableData,
  isError,
}: {
  chartData: any;
  isLoading: boolean;
  hasTableData: boolean;
  isError: boolean;
}) {
  const { chart_group_by, metric } = useSelector((state: RootState) => state.alarms);
  const componentColorMap = useMemo(() => generateComponentColorMap(chartData), [chartData]);

  if (isLoading) return <Skeleton className="min-h-0 h-full" />;

  return (
    <BaseCard
      title={
        <div className="w-full flex justify-between">
          <div className="capitalize text-sm">
            <CardDropdown
              defaultSelection={chartItems.find((el) => el.id === chart_group_by)}
              items={chartItems}
              onSelect={(data) => {
                const item = Array.isArray(data) ? data[0] : data;
                if (item) dispatch(setChartGroupBy(item.id));
              }}
            />
          </div>
        </div>
      }
      classNames={{
        title: 'p-0 pb-1',
        card: 'flex flex-col h-full',
        content: 'flex-1 flex h-full w-full min-h-0',
      }}
      variant="wrapper"
    >
      {!chartData || chartData.length === 0 || isError ? (
        <EmptyState
          text={
            isError ? (
              <div className="flex justify-center flex-col w-full items-center gap-2 ">
                <div>⚠️ Data could not be loaded.</div>
                <div>If the issue persists, please contact support.</div>
              </div>
            ) : undefined
          }
        />
      ) : (
        <StackedBarChartEcharts
          YAxisUnits={metric === 'duration' ? 's' : ''}
          chartData={chartData}
          colorByValueRank={false}
          componentColorMap={componentColorMap}
          barWidth={16}
          hideYAxis={false}
          hideXAxis={false}
          hideXAxisLine={true}
          gridOverwrite={{ top: 10, left: 0, right: 10, bottom: 5 }}
          legnedOverwrite={{ show: false }}
          renderTooltip={renderTopAlarmsTooltip({ chart_group_by, metric, colorMap: componentColorMap })}
        />
      )}
    </BaseCard>
  );
}
